import React, { useCallback, useEffect, useState } from "react";
import { AppConfig } from "../../config";

interface Product {
  id: string;
  name: string;
  description: string;
  originalPrice: number;
  imageUrl: string;
  available: boolean;
  price_type: Number;
  show_price_without_gst: Boolean;
  price: any;
  channel_unavailable_message: any;
  specifications: any;
  type: String;
  recommended: Boolean;
  channel_switch: any;
  channel_assigned: any;
  images: any;
  inventory_based_price: any;
  inventory_based_stock_price: any;
}

interface ProductCardProps {
  showVariants?: Boolean;
  isSuperAdmin?: Boolean;
  onRemove?: () => void;
  variantData?: {};
  product: Product;
  imageBaseUrl: any;
  onEdit: (data: any) => void;
  onAvailable: (data: any, event: any) => void;
  onRecommend: (data: any) => void;
  onAssign: (data: any) => void;
  triggerBottomsheet?: (data: any) => void;
}

const formatPrice = (price?: number) =>
  price !== undefined
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(price)
    : "";

const ProductCard: React.FC<ProductCardProps> = ({
  isSuperAdmin,
  showVariants,
  onRemove,
  variantData,
  product,
  imageBaseUrl,
  onEdit,
  onAvailable,
  onRecommend,
  onAssign,
  triggerBottomsheet,
}) => {
  const [productData, setProductData] = useState({
    ...product,
  });

  useEffect(() => {
    setProductData({ ...product });
  }, [product]);

  const dietIcon = (specifications: any[]) => {
    const spec = specifications?.find(
      (s) => s.id === AppConfig.diet_type.diet_type
    );
    const valueId = spec?.values?.[0]?.id;

    const icons: Record<string, string> = {
      [AppConfig.diet_type.vegetarian]: "e301ce3af353aab421a2f76c898a439d.png",
      [AppConfig.diet_type.non_vegetarian]:
        "5c7a11ae863a9b5cfa0e1844c7a186b9.png",
      [AppConfig.diet_type.eggetarian]: "fea28c272b8bbfb74f54b073bea5cfaa.png",
      [AppConfig.diet_type.vegan]: "1e2d32e8b1e3f2fd5eb65c3b8957674e.png",
    };

    if (valueId && icons[valueId]) {
      return (
        <img
          src={`${AppConfig.CDN_Image_url}${icons[valueId]}`}
          className="w-16px me-2"
          alt={`Diet type: ${valueId.replace("_", " ")}`}
        />
      );
    }

    return null;
  };

  const getPrice = (product: Product): string => {
    const isWithoutGST = product.show_price_without_gst;
    const p = product.price || {};

    switch (product.price_type) {
      case 1:
      case 2:
      case 3:
      case 5:
      case 7:
        return isWithoutGST && p.price_without_tax
          ? formatPrice(p.price_without_tax)
          : p.price_with_tax
          ? formatPrice(p.price_with_tax)
          : "";

      case 4: {
        const inv1 = product.inventory_based_price?.find(
          (i: any) => i.available
        );
        return isWithoutGST && inv1?.price?.price_without_tax
          ? formatPrice(inv1.price.price_without_tax)
          : inv1?.price?.price_with_tax
          ? formatPrice(inv1.price.price_with_tax)
          : "";
      }

      case 6: {
        const inv2 = product.inventory_based_stock_price?.find(
          (i: any) => i.available
        );
        return isWithoutGST && inv2?.price?.price_without_tax
          ? formatPrice(inv2.price.price_without_tax)
          : inv2?.price?.price_with_tax
          ? formatPrice(inv2.price.price_with_tax)
          : "";
      }

      default:
        return "";
    }
  };

  const getStrikePrice = (product: Product): string => {
    const isWithoutGST = product.show_price_without_gst;
    const p = product.price || {};

    switch (product.price_type) {
      case 1:
      case 2:
      case 3:
      case 5:
      case 7:
        return isWithoutGST && p.strike_price_without_tax
          ? formatPrice(p.strike_price_without_tax)
          : p.strike_price_with_tax
          ? formatPrice(p.strike_price_with_tax)
          : "";

      case 4: {
        const inv1 = product.inventory_based_price?.find(
          (i: any) => i.available
        );
        return isWithoutGST && inv1?.price?.strike_price_without_tax
          ? formatPrice(inv1.price.strike_price_without_tax)
          : inv1?.price?.strike_price_with_tax
          ? formatPrice(inv1.price.strike_price_with_tax)
          : "";
      }

      case 6: {
        const inv2 = product.inventory_based_stock_price?.find(
          (i: any) => i.available
        );
        return isWithoutGST && inv2?.price?.strike_price_without_tax
          ? formatPrice(inv2.price.strike_price_without_tax)
          : inv2?.price?.strike_price_with_tax
          ? formatPrice(inv2.price.strike_price_with_tax)
          : "";
      }

      default:
        return "";
    }
  };

  const hasAnyCheckedAddon = variantData?.addons?.some((group) => {
    return group.checked || group.children?.some((child) => child.checked);
  });

  const showUnderReview =
    // For both product and product_group
    Object.keys(productData?.updated_locales || {}).length > 0 ||
    Object.keys(productData?.updated_details || {}).length > 0 ||
    Object.keys(productData?.updated_specifications || {}).length > 0 ||
    // For product_group variants (if any)
    (productData.type === "product_group" &&
      productData?.product_variants?.some(
        (variant: any) =>
          Object.keys(variant.updated_locales || {}).length > 0 ||
          Object.keys(variant.updated_details || {}).length > 0 ||
          Object.keys(variant.updated_specifications || {}).length > 0
      ));

  return (
    <div className="card h-100 shadow-md radius-10">
      {/* varient */}
      {showVariants && (
        <div className="d-flex align-items-center border-bottom p-2">
          {variantData?.variant.map((sv_ele: any, sv_index: any) => (
            <div
              className="border-right px-1 text-center"
              key={`sv-${sv_index}`}
            >
              <div className="fs-10px pop-regular text-gray">{sv_ele.name}</div>
              <div className="fs-11px pop-font text-black-color">
                {sv_ele.items.name}
              </div>
            </div>
          ))}
          <div className="ms-auto">
            <div
              className="px-2 cursor"
              onClick={() => {
                onRemove();
              }}
            >
              <img
                src={
                  AppConfig.CDN_Image_url +
                  "01754678e0ae7200f18fc12a1a23f433.png"
                }
                className="w-14px"
              />
            </div>
          </div>
        </div>
      )}

      {/* header */}
      <div className="d-flex align-items-center px-2 pt-2">
        <div className="d-flex align-items-center px-1">
          <input
            type="checkbox"
            className="custom-check me-2"
            checked={productData.channel_assigned === "checked" ? true : false}
            onChange={() => {
              let sendData = {
                product_id: "",
                status: "",
                type: productData.type,
              };
              if (productData.type === "product") {
                sendData.product_id = productData?.product_id;
              } else {
                sendData.product_id = productData?.product_group_id;
              }
              if (productData.channel_assigned === "checked") {
                sendData.status = "2";
              } else {
                sendData.status = "1";
              }
              onAssign(sendData);
            }}
          />
          {dietIcon(productData?.specifications) ? (
            <div className="">{dietIcon(productData?.specifications)}</div>
          ) : (
            <div
              className="me-2"
              style={{
                width: "16px",
                height: "16px",
                backgroundColor: "#e5e7eb", // Tailwind gray-200
                borderRadius: "4px",
              }}
            />
          )}
          {/* {dietIcon(productData?.specifications)} */}
          <img
            src={
              productData.recommended
                ? `${AppConfig.CDN_Media_url}5b999c4d7e924181c4d7b3996f1ed6a4.svg`
                : `${AppConfig.CDN_Media_url}c6eb31a57426a97235e45ec31114c450.svg`
            }
            onClick={() => onRecommend(productData)}
            className="w-16px"
          />
        </div>
        <div className="d-flex ms-auto align-items-center">
          {/* Under Review Badge */}
          {showUnderReview && (
            <div
              className="mx-2"
              style={{
                marginBottom: "4px",
                backgroundColor: "#fef3c7", // soft light yellow
                color: "#92400e", // dark amber-800
                fontSize: "11px",
                fontWeight: 500,
                padding: "2px 6px",
                borderRadius: "4px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
              }}
            >
              Under Review
            </div>
          )}
          <label className="in-stock-switch cursor">
            <input
              type="checkbox"
              checked={productData.channel_switch === "on"}
              disabled={productData.channel_assigned === "unchecked"}
              onChange={(e) => onAvailable(productData, e)}
            />
            <span className="slider"></span>
          </label>
        </div>
      </div>
      {/* body */}
      <div
        className={`d-flex ${
          productData?.images?.length > 0 ? "px-2 pt-2" : "p-2"
        }`}
      >
        <div className="w-70per px-1 d-flex flex-column justify-between">
          <div>
            <div className="fs-14px pop-font fw-500">{productData.name}</div>
            <div className="d-flex align-items-center">
              <div className="fs-14px pop-font pe-2 text-dark">
                {getPrice(productData)}
              </div>
              <div className="pop-regular text-gray fs-11 strike">
                {getStrikePrice(productData)}
              </div>
            </div>
            <div className="v-small text-gray">{productData.description}</div>
          </div>

          <div
            className={`mt-auto py-1 d-flex align-items-center flex-wrap gap-1 ${
              isSuperAdmin ? "cursor" : ""
            }`}
            onClick={() => {
              if (isSuperAdmin) {
                let sendData = { from: "tag",
                  tagData : productData?.tags,
                  productData : productData,
                };
                triggerBottomsheet(sendData);
              }
            }}
          >
            <span className="fw-500 me-1">Tags:</span>
            {productData?.tags?.some((tag: any) => tag.checked) ? (
              productData?.tags
                .filter((tag: any) => tag.checked)
                .map((tag: any) => (
                  <span
                    key={tag.id}
                    className="badge bg-light text-dark border"
                  >
                    {tag.name}
                  </span>
                ))
            ) : (
              <span className="text-muted">No tags</span>
            )}
          </div>
        </div>
        <div className="w-30per">
          {productData?.images?.length > 0 && (
            <img
              src={`${imageBaseUrl}${productData.images[0]}`}
              className="added-prod-img"
            />
          )}
          <div
            className={`product-card-img-over-btn ${
              productData.hasOwnProperty("images")
                ? productData?.images?.length === 0
                  ? "bottom-0"
                  : "bottom-15"
                : "bottom-0"
            }`}
            onClick={() => onEdit(productData)}
          >
            <div className="fs-11px text-black pop-regular fw-500">
              {productData.type == "product_group" ? "Edit/Variant" : "Edit"}
            </div>
          </div>
        </div>
      </div>
      {/* footer */}
      {productData?.channel_unavailable_message && (
        <div className="bg-light-gray bottom-radius-10 p-2">
          <div className="sub-text text-dark">
            {productData?.channel_unavailable_message}
          </div>
        </div>
      )}
      {showVariants && hasAnyCheckedAddon && (
        <div className="p-2 border-top mt-2">
          <div className="fs-15px pop-font fw-500">Addon Groups</div>
          {variantData?.addons?.map?.(
            (av_ele: any, av_index: any) =>
              av_ele.checked && (
                <div key={`av-${av_index}`}>
                  <label className="mt-3 d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="custom-check me-2"
                      checked={av_ele.checked}
                    />
                    <div className="fs-13px pop-font text-blak fw-500">
                      {av_ele.name}
                    </div>
                  </label>
                  <div className="ps-3">
                    {av_ele.children.map((avc_ele: any, avc_index: any) => (
                      <label className="mt-3 d-flex align-items-center">
                        <input
                          type="checkbox"
                          className="custom-check me-2"
                          checked={avc_ele.checked}
                        />
                        <div className="fs-12px pop-regular fw-500">
                          {avc_ele.name}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCard;
